import React from 'react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useStore } from '@/store/useStore';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { GlassTabbar } from '@/components/ui/GlassTabbar';
import type { GlassTab } from '@/components/ui/GlassTabbar';

// Original Application SVG Icons (currentColor -> tinted by the option state)
const HomeIcon = ({ isActive }: { isActive: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 20" className={cn("w-5 h-5 transition-all duration-300", isActive ? "scale-110" : "")} style={{ fill: 'currentColor' }}>
    <path d="M18.9999 6.01002L12.4499 0.770018C11.1699 -0.249982 9.16988 -0.259982 7.89988 0.760018L1.34988 6.01002C0.409885 6.76002 -0.160115 8.26002 0.0398848 9.44002L1.29988 16.98C1.58988 18.67 3.15988 20 4.86988 20H15.4699C17.1599 20 18.7599 18.64 19.0499 16.97L20.3099 9.43002C20.4899 8.26002 19.9199 6.76002 18.9999 6.01002ZM10.9199 16C10.9199 16.41 10.5799 16.75 10.1699 16.75C9.75988 16.75 9.41988 16.41 9.41988 16V13C9.41988 12.59 9.75988 12.25 10.1699 12.25C10.5799 12.25 10.9199 12.59 10.9199 13V16Z" />
  </svg>
);

// Kite app mark (kite.svg) → currentColor so the tab tints active/inactive
// viewBox cropped to the mark's bounds (the 2048 box is mostly empty) so it fills the 24px slot
const StoreIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="690 455 670 1140" className="w-[22px] h-[22px] transition-all duration-300" style={{ fill: 'currentColor' }}>
    <path d="M 1023.13 523.476 C 1025.2 524.558 1025.94 525.306 1026.97 527.514 C 1037.28 549.418 1047.23 571.85 1057.27 593.847 L 1120.28 731.571 L 1316.67 1161.24 C 1307 1174.69 1292.46 1191.73 1281.77 1205.08 L 1216.46 1286.7 L 1024.48 1526.49 C 1015.29 1519.92 963.468 1451.53 953.171 1438.61 L 814.934 1265.91 C 787.862 1232.04 759.106 1194.7 731.286 1161.74 C 738.408 1143.94 747.589 1125.07 755.654 1107.54 L 790.785 1030.68 L 910.59 768.633 L 986.725 602.073 L 1009.4 552.308 C 1013.12 544.11 1018.8 530.639 1023.13 523.476 z M 877.737 1161.36 L 949.477 1250.94 C 959.989 1264.09 976.742 1286.34 987.956 1298.36 C 987.928 1323.33 986.729 1365.86 989.225 1389.95 C 989.656 1394.12 1019.02 1429.59 1023.87 1434.63 C 1025.2 1321.86 1023.97 1206.43 1023.99 1093.5 C 1024 1076.51 1022.03 957.821 1025.52 950.971 L 1026.68 951.516 L 1026.74 953.137 C 1027.35 967.954 1032.8 1002.9 1034.84 1020.02 L 1056.06 1192.75 C 1059.38 1219.02 1066.17 1262.16 1067.21 1287.5 C 1073.66 1282.93 1085.84 1266.53 1091.34 1259.64 L 1142.69 1195.33 C 1149.33 1187.02 1163.53 1168.28 1170.23 1161.97 C 1159.03 1132.63 1028.23 802.191 1023.54 797.607 C 1015.21 816.551 1007.49 837.39 999.74 856.719 L 956.296 965.008 L 900.726 1103.87 C 893.355 1122.41 884.646 1142.85 877.737 1161.36 z" />
  </svg>
);

const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-5 h-5 transition-all duration-300" style={{ fill: 'currentColor' }}>
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

// ─── EXACT STAKING SVG FROM DEV.PORTALS-MARKET.COM ─── //
const SvgStaking = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 transition-all duration-300">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.44873 2.67537C10.3605 0.905475 12.2533 0.451782 13.245 0.496604C13.9563 0.528747 14.2767 1.16028 14.2767 1.64162V3.89603C15.1773 4.34879 15.7797 4.95236 16.1493 5.7046C16.5138 6.44632 16.6244 7.27936 16.6438 8.13332L17.5374 8.86729C17.686 8.9894 17.7721 9.17167 17.7721 9.36404V12.2167C17.7721 12.3103 17.7517 12.4028 17.7122 12.4877C17.008 14.0027 15.7595 14.8213 14.2767 15.4022V16.2213C14.2767 16.9313 13.7011 17.507 12.991 17.507H10.7411C10.031 17.507 9.45538 16.9313 9.45538 16.2213V15.5985H6.70314V16.1208C6.70314 16.8309 6.12751 17.4065 5.41742 17.4065H3.14729C2.43805 17.4065 1.86148 16.8324 1.86156 16.1213C1.86164 15.3975 1.86173 14.2756 1.86154 13.9137C1.85089 13.8774 1.83519 13.8272 1.81335 13.7585L1.78771 13.678C1.74212 13.5352 1.68262 13.3488 1.61739 13.1316L1.61166 13.1124C1.17024 11.6424 0.350539 8.91261 1.52519 6.3092C1.61266 6.11532 1.71095 5.9255 1.82082 5.74068C1.21353 5.26005 0.604741 4.58131 0.266993 3.67622C0.111834 3.26043 0.32312 2.79758 0.738913 2.64242C1.15471 2.48726 1.61756 2.69855 1.77272 3.11434C1.98382 3.68008 2.3835 4.1408 2.8376 4.49576C3.24205 4.12877 3.72169 3.80343 4.28639 3.53285C5.56834 2.91859 7.25677 2.60114 9.44873 2.67537ZM9.48288 6.30275C9.48288 6.74656 9.12311 7.10633 8.67931 7.10633H6.29433C5.85053 7.10633 5.49076 6.74656 5.49076 6.30275C5.49076 5.85895 5.85053 5.49918 6.29433 5.49918H8.67931C9.12311 5.49918 9.48288 5.85895 9.48288 6.30275Z"
      fill="currentColor"
    />
  </svg>
);

interface TabNavigatorProps {
  activeTab: string;
  onNavigate: (tab: string) => void;
  unreadCount?: number;
  hidden?: boolean;
}

export function TabNavigator({ activeTab, onNavigate, unreadCount, hidden }: TabNavigatorProps) {
  const { selectionChanged } = useHapticFeedback();
  const { t } = useTranslation('nav');
  const user = useStore(state => state.user);
  const [keyboardVisible, setKeyboardVisible] = React.useState(false);

  React.useEffect(() => {
    const handleFocusIn = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        setKeyboardVisible(true);
      }
    };
    const handleFocusOut = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        setKeyboardVisible(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const go = (tab: string) => {
    selectionChanged();
    onNavigate(tab);
  };

  const renderMiddle: GlassTab[] = [
    { id: 'store', label: t('store'), icon: <StoreIcon /> },
    { id: 'my_user', label: t('myUsers'), icon: <UserIcon /> },
    { id: 'staking', label: t('staking'), icon: <SvgStaking /> },
  ];

  const MIDDLE_IDS = new Set(['store', 'my_user', 'staking']);

  return (
    <div className="fixed bottom-0 left-1/2 -translate-x-1/2 z-50 w-full max-w-[512px] px-4 pt-3 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pointer-events-none">
      <motion.div
        animate={{ y: hidden || keyboardVisible ? 140 : 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 34 }}
        style={{ pointerEvents: hidden || keyboardVisible ? 'none' : 'auto' }}
      >
        <GlassTabbar
          tabs={renderMiddle}
          activeId={MIDDLE_IDS.has(activeTab) ? activeTab : '__none__'}
          onSelect={go}
          accent="#3390EC"
          leftSlot={
            <div
              role="button"
              tabIndex={0}
              onClick={() => go('home')}
              className={cn('gt-circle', activeTab === 'home' && 'active')}
            >
              <span className="gt-icon" style={{ color: activeTab === 'home' ? '#fff' : '#ffffff66' }}>
                <HomeIcon isActive={activeTab === 'home'} />
              </span>
            </div>
          }
          rightSlot={
            <div
              role="button"
              tabIndex={0}
              onClick={() => go('profile')}
              className={cn('gt-circle', activeTab === 'profile' && 'active')}
            >
              <div className="w-9 h-9 gt-profile-avatar overflow-hidden">
                {user?.photo_url ? (
                  <img src={user.photo_url} alt="profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/10">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 fill-white/70">
                      <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12ZM4 20C4 16.6863 6.68629 14 10 14H14C17.3137 14 20 16.6863 20 20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20Z" />
                    </svg>
                  </div>
                )}
              </div>
              {Number(unreadCount) > 0 && (
                <span className="absolute top-0 right-1 w-2 h-2 rounded-full bg-red-500 animate-ping" />
              )}
            </div>
          }
        />
      </motion.div>
    </div>
  );
}

export default TabNavigator;